/**
 * @file useDragSelect.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-04-23
 * @modified 2026-10-12
 * @description Tick boxes in a table by clicking, dragging across rows, or
 *              (1.1.0, 2026-10-12) shift-clicking: a shift-click sets every
 *              row from the last row clicked to this one, in the order the
 *              rows are shown, to the state the clicked row takes. Pass
 *              `orderedIds` (the rows on screen, in order) to have shift-click;
 *              callers that do not pass it behave as before.
 */

import { useEffect, useRef, useCallback } from 'react';

/**
 * The ids from `a` to `b` inclusive, in `order`'s order (either direction);
 * just `[b]` when either is not in `order`.
 */
export function rangeBetween(order, a, b) {
  const list = Array.isArray(order) ? order : [];
  const i = list.indexOf(a);
  const j = list.indexOf(b);
  if (i < 0 || j < 0) return [b];
  return i <= j ? list.slice(i, j + 1) : list.slice(j, i + 1);
}

// Caller supplies setChecked(id, shouldBeChecked) so the hook is agnostic to
// whether the underlying selection state is a Set, array or keyed object.
export function useDragSelect({ setChecked, orderedIds = null }) {
  const dragRef = useRef(null);
  // The last row clicked without shift: where a shift-click range starts.
  const anchorRef = useRef(null);
  const orderRef = useRef(orderedIds);
  orderRef.current = orderedIds;

  const onMouseDown = useCallback((itemId, currentlyChecked) => (e) => {
    if (e.button !== 0) return;
    // Prevent the label's native behaviour of dispatching a click on the hidden
    // input; we apply state ourselves so single-click and drag-select go through
    // the same path.
    e.preventDefault();

    const targetChecked = !currentlyChecked;
    const ids = (e.shiftKey && orderRef.current && anchorRef.current !== null)
      ? rangeBetween(orderRef.current, anchorRef.current, itemId)
      : [itemId];
    if (!e.shiftKey) anchorRef.current = itemId;
    dragRef.current = {
      targetChecked,
      processed: new Set(ids),
    };
    for (const id of ids) setChecked(id, targetChecked);
  }, [setChecked]);

  const onMouseEnter = useCallback((itemId) => () => {
    const drag = dragRef.current;
    if (!drag) return;
    if (drag.processed.has(itemId)) return;
    drag.processed.add(itemId);
    setChecked(itemId, drag.targetChecked);
  }, [setChecked]);

  useEffect(() => {
    const clear = () => { dragRef.current = null; };
    window.addEventListener('mouseup', clear);
    window.addEventListener('blur', clear);
    return () => {
      window.removeEventListener('mouseup', clear);
      window.removeEventListener('blur', clear);
    };
  }, []);

  return { onMouseDown, onMouseEnter };
}

export default useDragSelect;
